import React from 'react';
import { ChevronDown, ChevronRight, Target, Edit, ArrowLeft, Link as LinkIcon, Trash2, Loader2, RotateCw, Printer } from 'lucide-react';
import { Card } from '../../ui/card';
import { Button } from '../../ui/button';
import { EditAssessment } from '../EditAssessment2/EditAssessment';
import { AssessmentStatusDto, UnitDto } from './types';
import { AssessmentPreviewSection } from './AssessmentPreviewSection';
import { AssessmentReviewSection } from './AssessmentReviewSection';
import { RevealedLinkDisplay } from './RevealedLinkDisplay';
import { useLazyCommand, useEvent, useLazyQuery } from '@soap/modules';
import { useSelectedStudent } from '../../hooks/useSelectedStudent';
import { DeleteAssessmentDialog } from '../dialogs/DeleteAssessmentDialog';
import { DeleteAssessmentResultsDialog } from '../dialogs/DeleteAssessmentResultsDialog';
import { ShareByEmailDialog } from '../ShareByEmailDialog';
import { UnitStateKey } from './lessonStepState';
import { buildAssessmentPrintHtml, openAssessmentPrintWindow } from './printAssessmentDocument';
import { confirmActionOutlineButtonClass } from '../../constants/brandPalette';
import { useFormatDisplayDate } from '../../../utils/useFormatDisplayDate';

interface AssessmentCardProps {
    unit: UnitDto;
    status: AssessmentStatusDto | undefined;
    isExpanded: boolean;
    onToggle: (assessmentId: string) => void;
    /** Required for live E172/E174/E180/E270 status — see onRefresh comment on the useEvent block. */
    onRefresh?: () => void;
    isDeleting?: boolean;
    passFailThreshold?: number | null;
}

export function AssessmentCard({
    unit,
    status,
    isExpanded,
    onToggle,
    onRefresh,
    isDeleting: isDeletingProp,
    passFailThreshold = null
}: AssessmentCardProps) {
    const formatDate = useFormatDisplayDate();
    if (!unit.e251_AssessmentId) return null;
    
    const viewStates = {
        viewing: 0,
        editing: 1,
        deletingAssessment: 2
    };
    
    const [viewState, setViewState] = React.useState(viewStates.viewing);
    const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
    const [deleteResultsDialogOpen, setDeleteResultsDialogOpen] = React.useState(false);
    const [isDeleting, setIsDeleting] = React.useState(isDeletingProp || false);
    const [isRetaking, setIsRetaking] = React.useState(false);
    const [shareByEmailDialogOpen, setShareByEmailDialogOpen] = React.useState(false);
    const [isPrinting, setIsPrinting] = React.useState(false);
    
    const [getAssessment, assessmentQueryResult] = useLazyQuery('C188v1_GetAssessment', null);
    const [getAssessmentResult, assessmentResultQueryResult] = useLazyQuery('C219v1_GetAssessmentResult', null);
    
    const [sendMarkAssessmentCompletedCommand] = useLazyCommand('C280v1_MarkAssessmentCompleted');
    const [sendCancelLinkCommand] = useLazyCommand('C210v1_CancelLink');
    const [sendRetakeAssessmentCommand] = useLazyCommand('C283v1_RetakeAssessment');
    const [shareByStudent] = useLazyCommand('C203v1_ShareByStudent');
    const [shareByLink] = useLazyCommand('C201v1_ShareByLink');
    
    const { selectedStudentId, students } = useSelectedStudent();
    
    // Sync isDeleting prop to state
    React.useEffect(() => {
        if (isDeletingProp !== undefined) {
            setIsDeleting(isDeletingProp);
        }
    }, [isDeletingProp]);
    
    // Listen for assessment cancelled event to reset viewState and isDeleting
    useEvent({
        eventName: 'E169v1_AssessmentCancelled',
        onEventReceived: (event: any) => {
            const assessmentId = event.e169_AssessmentId ?? event.E169_AssessmentId;
            const unitId = event.e169_UnitId ?? event.E169_UnitId;
            if (assessmentId === unit.e251_AssessmentId || unitId === unit.e251_UnitId) {
                setViewState(viewStates.viewing);
                setIsDeleting(false);
            }
        }
    });
    
    /*
     * DO NOT remove onRefresh() from these assessment SignalR handlers (E172 / E174 / E180 / E270).
     *
     * LessonsPage also applies optimistic unit patches + C251v1_GetUnits, but that path alone has
     * already regressed (Jul 2026): deleting results or completing an assessment in another window
     * left this card stale until a hard refresh. The card-level onRefresh → GetUnits call is the
     * required safety net so status always reconciles from the server after those events.
     *
     * Keep both: page optimistic patch (instant UI) AND this onRefresh (authoritative refresh).
     * Removing “redundant” onRefresh here will re-break live status on the unit card.
     */
    const assessmentEventMatchesThisCard = (event: Record<string, unknown>, prefixes: string[]) => {
        const pick = (...keys: string[]) => {
            for (const k of keys) {
                const v = event[k];
                if (v != null && String(v).trim() !== '') return String(v);
            }
            return undefined;
        };
        for (const p of prefixes) {
            const assessmentId = pick(`${p}_AssessmentId`, `${p.toLowerCase()}_AssessmentId`);
            const unitId = pick(`${p}_UnitId`, `${p.toLowerCase()}_UnitId`);
            const resultId = pick(`${p}_AssessmentResultId`, `${p.toLowerCase()}_AssessmentResultId`);
            if (
                (assessmentId && assessmentId === unit.e251_AssessmentId) ||
                (unitId && unitId === unit.e251_UnitId) ||
                (resultId && resultId === status?.e251_Result?.e251_AssessmentResultId)
            ) {
                return true;
            }
        }
        return false;
    };

    // E172 — started elsewhere (student portal / other window). Keep onRefresh (see block above).
    useEvent({
        eventName: 'E172v1_AssessmentStarted',
        onEventReceived: (event: any) => {
            if (assessmentEventMatchesThisCard(event, ['E172', 'e172'])) {
                onRefresh?.();
            }
        }
    });

    // E174 — completed elsewhere → Needs Marking. Keep onRefresh (see block above).
    useEvent({
        eventName: 'E174v1_AssessmentCompleted',
        onEventReceived: (event: any) => {
            if (assessmentEventMatchesThisCard(event, ['E174', 'e174'])) {
                onRefresh?.();
            }
        }
    });

    // E180 — approved: expand review UX + keep onRefresh (see block above).
    useEvent({
        eventName: 'E180v1_AssessmentApproved',
        onEventReceived: (event: any) => {
            if (
                assessmentEventMatchesThisCard(event, ['E180', 'e180'])
            ) {
                if (unit.e251_AssessmentId && !isExpanded) {
                    onToggle(unit.e251_AssessmentId);
                }
                if (status?.e251_Result?.e251_AssessmentResultId) {
                    const reviewId = status.e251_Result.e251_AssessmentResultId;
                    setExpandedAssessmentReviews(prev => {
                        const newSet = new Set(prev);
                        newSet.add(reviewId);
                        return newSet;
                    });
                }
                onRefresh?.();
            }
        }
    });
    
    // Listen for assessment created event to handle retake completion
    useEvent({
        eventName: 'E264v1_AssessmentCreated',
        onEventReceived: (event: any) => {
            // Check if this is for a retake (same assessment ID, different unit ID)
            if (isRetaking && event.E264_AssessmentId === unit.e251_AssessmentId && event.E264_UnitId !== unit.e251_UnitId) {
                setIsRetaking(false);
                onRefresh?.();
                // Navigate to in-progress tab after retake is created
                window.location.hash = '#/app/lessons/inprogress';
            }
        }
    });
    
    // E270 — results deleted (same window or elsewhere). Keep onRefresh (see block above).
    useEvent({
        eventName: 'E270v1_AssessmentResultsDeleted',
        onEventReceived: (event: any) => {
            if (assessmentEventMatchesThisCard(event, ['E270', 'e270'])) {
                setDeleteResultsDialogOpen(false);
                onRefresh?.();
            }
        }
    });
    
    const resultState = status?.e251_Result?.e251_ResultState;
    const shared = !!status?.e251_SharedAssessment;
    const started = !!status?.e251_Result;
    const isUnitCompleted = unit.e251_State === UnitStateKey.Complete;
    // Local state for managing assessment UI
    const [isLinkRevealed, setIsLinkRevealed] = React.useState(false);
    const [expandedAssessmentReviews, setExpandedAssessmentReviews] = React.useState<Set<string>>(new Set());
    const [assessmentPreviews, setAssessmentPreviews] = React.useState<Map<string, any>>(new Map());
    const [assessmentResults, setAssessmentResults] = React.useState<Map<string, any>>(new Map());
    /** Incremented on E149 so preview panel ignores stale useLazyQuery data until E154 repopulates the map. */
    const [previewEpoch, setPreviewEpoch] = React.useState(0);

    // Drop cached preview only (no unit refresh) so expanded preview refetches without closing edit mode elsewhere
    useEvent({
        eventName: 'E149v1_AssessmentUpdated',
        onEventReceived: (event: any) => {
            const id = event.e149_AssessmentId ?? event.E149_AssessmentId;
            if (id === unit.e251_AssessmentId) {
                setPreviewEpoch((e) => e + 1);
                setAssessmentPreviews((prev) => {
                    const next = new Map(prev);
                    next.delete(unit.e251_AssessmentId!);
                    return next;
                });
            }
        }
    });
    
    // Automatically expand review section when unit is completed and has assessment results
    React.useEffect(() => {
        if (isUnitCompleted && status?.e251_Result?.e251_AssessmentResultId && isExpanded) {
            setExpandedAssessmentReviews(prev => {
                const newSet = new Set(prev);
                newSet.add(status.e251_Result.e251_AssessmentResultId);
                return newSet;
            });
        }
    }, [isUnitCompleted, status?.e251_Result?.e251_AssessmentResultId, isExpanded]);
    
    const toggleFlip = () => {
        setViewState(prev => prev === viewStates.viewing ? viewStates.editing : viewStates.viewing);
    };
    
    const toggleAssessmentReview = (assessmentResultId: string) => {
        setExpandedAssessmentReviews(prev => {
            const newSet = new Set(prev);
            if (newSet.has(assessmentResultId)) {
                newSet.delete(assessmentResultId);
            } else {
                newSet.add(assessmentResultId);
            }
            return newSet;
        });
    };
    
    // Get link from status - one link per sharedAssessmentId
    const assessmentLink = status?.e251_SharedAssessment?.e251_LinkUrl || null;
    
    // Prefer result status when present so shared assessments don't keep showing
    // "Waiting for Results" after the student finishes (e.g. another browser window).
    const displayMessage = started
        ? (resultState?.value ?? null)
        : shared
        ? (status?.e251_SharedAssessment?.e251_ShareType === "by-email" 
            ? "Assigned. Email Sent. Waiting for Results."
            : status?.e251_SharedAssessment?.e251_ShareType === "by-student"
            ? "Assigned to Student Portal. Waiting for Results."
            : status?.e251_SharedAssessment?.e251_ShareType === "by-link"
            ? "Link Created. Waiting for Results."
            : null)
        : null;
    // Filter out meaningless values like just a period
    const meaningfulDisplayMessage = displayMessage && displayMessage.trim() && displayMessage.trim() !== '.' ? displayMessage.trim() : null;
    
    // Check if this is an assessment-only unit (no lessons, has assessment)
    const lessons = unit.e251_Lessons || [];
    const isAssessmentOnlyUnit = lessons.length === 0 && unit.e251_AssessmentId != null;
    
    // Determine if we should show Delete button
    // Hide Delete button when results exist (except TeacherApproved)
    // Hide Delete button for assessment-only units (users should use Abandon Unit instead)
    const shouldShowDelete = !isUnitCompleted && 
        (!started || resultState?.key === 'teacher-approved') &&
        !isAssessmentOnlyUnit;
    
    // Determine if we should show Delete Results button
    const shouldShowDeleteResults = started && 
        resultState?.key !== 'teacher-approved' && 
        !isUnitCompleted;
    
    // Determine if we should show Review Results button
    const shouldShowReviewResults = resultState?.key === 'needs-marking';
    
    const handlePrintAssessment = async (e: React.MouseEvent) => {
        e.stopPropagation();
        if (!unit.e251_AssessmentId || isPrinting) return;

        setIsPrinting(true);
        try {
            const assessmentId = unit.e251_AssessmentId;
            let assessment = assessmentPreviews.get(assessmentId) as Record<string, unknown> | undefined;
            if (!assessment) {
                await getAssessment({ C188_AssessmentId: assessmentId }, 0);
                assessment =
                    (assessmentPreviews.get(assessmentId) as Record<string, unknown> | undefined) ??
                    (assessmentQueryResult as Record<string, unknown> | undefined);
            }
            if (!assessment) return;

            const resultId = status?.e251_Result?.e251_AssessmentResultId;
            let result: Record<string, unknown> | undefined;
            if (resultId) {
                result = assessmentResults.get(resultId) as Record<string, unknown> | undefined;
                if (!result) {
                    await getAssessmentResult({ C219_AssessmentResultId: resultId }, 0);
                    result =
                        (assessmentResults.get(resultId) as Record<string, unknown> | undefined) ??
                        (assessmentResultQueryResult as Record<string, unknown> | undefined);
                }
            }

            const assessmentTitle =
                (assessment.e154_Title as string | undefined) ||
                (assessment.E154_Title as string | undefined) ||
                unit.e251_Title ||
                'Assessment';

            const html = buildAssessmentPrintHtml({
                title: assessmentTitle,
                assessment,
                result: result ?? null,
                statusLabel: meaningfulDisplayMessage,
                completedAt: status?.e251_Result?.e251_ResultCompletedAt ?? null,
                scorePercent: status?.e251_Result?.e251_Score ?? null,
            });
            openAssessmentPrintWindow(html);
        } finally {
            setIsPrinting(false);
        }
    };

    const assessmentPrintButton = (
        <Button
            variant="outline"
            size="sm"
            onClick={(e) => {
                void handlePrintAssessment(e);
            }}
            disabled={isPrinting}
            className="text-gray-600 border-gray-200 hover:bg-gray-100"
        >
            {isPrinting ? (
                <Loader2 size={14} className="mr-1 animate-spin" />
            ) : (
                <Printer size={14} className="mr-1" />
            )}
            Print
        </Button>
    );

    // Handle auto-sharing when Assign button is clicked
    const handleAssignAssessment = async () => {
        if (!unit.e251_AssessmentId || !selectedStudentId) return;
        
        const selectedStudent = students.find(s => s.id === selectedStudentId);
        
        if (selectedStudent?.hasOwnAccount) {
            // Share by student portal
            await shareByStudent({
                C203_AssessmentId: unit.e251_AssessmentId,
                C203_UnitId: unit.e251_UnitId,
                C203_StudentId: selectedStudentId,
                C203_Expiry: null
            });
        } else if (selectedStudent?.email) {
            // Share by email - open dialog for customization
            setShareByEmailDialogOpen(true);
        } else {
            // Share by link
            await shareByLink({
                C201_AssessmentId: unit.e251_AssessmentId,
                C201_UnitId: unit.e251_UnitId,
                C201_StudentId: selectedStudentId,
                C201_Expiry: null
            });
        }
    };
    
    return (
        <div className="mb-6">
            <Card className={`relative transition-all duration-300 border-2 ${
                resultState?.key === "teacher-approved" 
                    ? 'bg-confirm-action/10 border-confirm-action/30' 
                    : resultState?.key === "needs-marking"
                    ? 'bg-action-blue/10 border-action-blue/30'
                    : shared
                    ? 'bg-gray-50 border-gray-200'
                    : 'bg-action-blue/10 border-action-blue/30'
            } ${viewState === viewStates.editing ? 'h-auto' : ''}`}>
                <div>
                    {/* Front side - Assessment Card */}
                    <div className={`${viewState === viewStates.editing ? 'hidden' : 'block'}`}>
                        {/* Assessment Header - Clickable */}
                        <div
                            onClick={() => onToggle(unit.e251_AssessmentId!)}
                            className="w-full px-4 py-3 text-left transition-colors cursor-pointer hover:bg-opacity-80"
                        >
                            {/* Mobile: Buttons on top */}
                            <div className="md:hidden mb-3" onClick={(e) => e.stopPropagation()}>
                                <div className="flex flex-col gap-2">
                                    {assessmentPrintButton}

                                    {/* Show Edit button when unit has assessment and no assessment results exist yet */}
                                    {/* Show for: Assessing (60) state */}
                                    {unit.e251_AssessmentId && 
                                     unit.e251_State === UnitStateKey.Assessing && 
                                     !status?.e251_Result && (
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                toggleFlip();
                                            }}
                                            className="text-gray-600 border-gray-200 hover:bg-gray-100"
                                        >
                                            <Edit size={14} className="mr-1" />
                                            Edit
                                        </Button>
                                    )}
                                    
                                    {/* Show Assign button when no-link */}
                                    {(!shared && !started) && (
                                        <Button
                                            variant="default"
                                            size="sm"
                                            onClick={async (e) => {
                                                e.stopPropagation();
                                                await handleAssignAssessment();
                                            }}
                                            className="bg-action-blue hover:bg-action-blue-dark text-white"
                                        >
                                            Assign
                                        </Button>
                                    )}
                                    
                                    {/* Show Reveal/Hide Link and Cancel Link buttons when link-created */}
                                    {shared && (
                                        <>
                                            {assessmentLink && (
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        setIsLinkRevealed(prev => !prev);
                                                    }}
                                                    onMouseDown={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                    }}
                                                    className="text-action-blue border-action-blue/30 hover:bg-action-blue/15"
                                                >
                                                    <LinkIcon size={14} className="mr-1" />
                                                    {isLinkRevealed ? 'Hide Link' : 'Reveal Link'}
                                                </Button>
                                            )}
                                            {status?.e251_SharedAssessment?.e251_SharedAssessmentId && (
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={async (e) => {
                                                        e.stopPropagation();
                                                        if (status.e251_SharedAssessment?.e251_SharedAssessmentId) {
                                                            await sendCancelLinkCommand({
                                                                C210_SharedAssessmentId: status.e251_SharedAssessment.e251_SharedAssessmentId
                                                            });
                                                            if (onRefresh) {
                                                                onRefresh();
                                                            }
                                                        }
                                                    }}
                                                    className="text-destructive-action border-destructive-action/30 hover:bg-destructive-action/10"
                                                >
                                                    Cancel Link
                                                </Button>
                                            )}
                                        </>
                                    )}
                                    
                                    {/* Show Delete button at all times except when unit is completed */}
                                    {shouldShowDelete && unit.e251_UnitId && viewState !== viewStates.deletingAssessment && (
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setViewState(viewStates.deletingAssessment);
                                                setIsDeleting(true);
                                                setDeleteDialogOpen(true);
                                            }}
                                            className="text-destructive-action border-destructive-action/30 hover:bg-destructive-action/10"
                                        >
                                            <Trash2 size={14} className="mr-1" />
                                            Delete
                                        </Button>
                                    )}
                                    
                                    {/* Show deleting state */}
                                    {viewState === viewStates.deletingAssessment && (
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            disabled
                                            className="text-destructive-action border-destructive-action/30 hover:bg-destructive-action/10 opacity-50"
                                        >
                                            <Loader2 size={14} className="mr-1 animate-spin" />
                                            Deleting...
                                        </Button>
                                    )}
                                    
                                    {/* Show Delete Results button when started but not teacher-approved */}
                                    {shouldShowDeleteResults && status?.e251_Result?.e251_AssessmentResultId && (
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setDeleteResultsDialogOpen(true);
                                            }}
                                            className="text-destructive-action border-destructive-action/30 hover:bg-destructive-action/10"
                                        >
                                            <Trash2 size={14} className="mr-1" />
                                            Delete Results
                                        </Button>
                                    )}
                                    
                                    {/* Show Review Results button when started and needs-marking */}
                                    {shouldShowReviewResults && status?.e251_Result?.e251_AssessmentResultId && (
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                // Expand the card if it's collapsed
                                                if (!isExpanded && unit.e251_AssessmentId) {
                                                    onToggle(unit.e251_AssessmentId);
                                                }
                                                toggleAssessmentReview(status.e251_Result.e251_AssessmentResultId);
                                            }}
                                            className={confirmActionOutlineButtonClass}
                                        >
                                            {expandedAssessmentReviews.has(status.e251_Result.e251_AssessmentResultId) 
                                                ? 'Hide Answers' 
                                                : 'Review Results'}
                                        </Button>
                                    )}
                                    
                                    {/* Show Mark Assessment Completed button when in needs-marking state */}
                                    {resultState?.key === "needs-marking" && (
                                        <Button
                                            variant="default"
                                            size="sm"
                                            onClick={async (e) => {
                                                e.stopPropagation();
                                                if (status?.e251_Result?.e251_AssessmentResultId) {
                                                    await sendMarkAssessmentCompletedCommand({
                                                        C280_AssessmentResultId: status.e251_Result.e251_AssessmentResultId
                                                    });
                                                    onRefresh?.();
                                                }
                                            }}
                                            className="bg-action-blue hover:bg-action-blue-dark text-white"
                                        >
                                            Mark Assessment Completed
                                        </Button>
                                    )}
                                    
                                    {/* Show Retake button when unit is completed and assessment is teacher-approved */}
                                    {isUnitCompleted && resultState?.key === "teacher-approved" && unit.e251_AssessmentId && unit.e251_UnitId && unit.e251_StudentId && (
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            disabled={isRetaking}
                                            onClick={async (e) => {
                                                e.stopPropagation();
                                                setIsRetaking(true);
                                                try {
                                                    await sendRetakeAssessmentCommand({
                                                        C283_AssessmentId: unit.e251_AssessmentId,
                                                        C283_UnitId: unit.e251_UnitId,
                                                        C283_StudentId: unit.e251_StudentId
                                                    });
                                                } catch (error) {
                                                    setIsRetaking(false);
                                                }
                                                // Navigation and refresh will happen when E264v1_AssessmentCreated event is received
                                            }}
                                            className="text-action-blue border-action-blue/30 hover:bg-action-blue/15"
                                        >
                                            {isRetaking ? (
                                                <>
                                                    <Loader2 size={14} className="mr-1 animate-spin" />
                                                    Retaking...
                                                </>
                                            ) : (
                                                <>
                                                    <RotateCw size={14} className="mr-1" />
                                                    Retake
                                                </>
                                            )}
                                        </Button>
                                    )}
                                </div>
                            </div>

                            {/* Title and Details Section */}
                            <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-3 flex-1">
                                    {isExpanded ? (
                                        <ChevronDown size={16} className="text-gray-500" />
                                    ) : (
                                        <ChevronRight size={16} className="text-gray-500" />
                                    )}
                                    <Target size={20} className={
                                        resultState?.key === "teacher-approved"
                                            ? 'text-confirm-action'
                                            : resultState?.key === "needs-marking"
                                            ? 'text-action-blue'
                                            : shared
                                            ? 'text-action-blue'
                                            : 'text-gray-600'
                                    } />
                                    <div className="flex-1">
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <h4 className="text-sm font-semibold text-gray-900">
                                                Assessment{meaningfulDisplayMessage ? ` - ${meaningfulDisplayMessage}` : ''}
                                            </h4>
                                            {status?.e251_Result?.e251_Score != null && (
                                                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                                        status.e251_Result.e251_Score < (passFailThreshold ?? 50)
                                                            ? 'bg-orange-100 text-orange-700'
                                                            : 'bg-confirm-action/15 text-confirm-action-dark'
                                                    }`}>
                                                        Score: {status.e251_Result.e251_Score}%
                                                    </span>
                                                )}
                                        </div>
                                        {(resultState?.key === "teacher-approved" || resultState?.key === "needs-marking") && 
                                            status?.e251_Result?.e251_ResultCompletedAt && (
                                            <p className="text-xs text-gray-600 mt-1">
                                                Completed on {formatDate(status.e251_Result.e251_CompletedAt)}
                                            </p>
                                        )}
                                    </div>
                                </div>
                                
                                {/* Tablet/Desktop: Buttons on right (stacked vertically on tablet, horizontal on desktop) */}
                                <div className="hidden md:flex md:flex-col lg:flex-row md:items-stretch lg:items-center gap-2 ml-4" onClick={(e) => e.stopPropagation()}>
                                    {assessmentPrintButton}

                                    {/* Show Edit button when unit has assessment and no assessment results exist yet */}
                                    {/* Show for: Assessing (60) state */}
                                    {unit.e251_AssessmentId && 
                                     unit.e251_State === UnitStateKey.Assessing && 
                                     !status?.e251_Result && (
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                toggleFlip();
                                            }}
                                            className="text-gray-600 border-gray-200 hover:bg-gray-100"
                                        >
                                            <Edit size={14} className="mr-1" />
                                            Edit
                                        </Button>
                                    )}
                                    
                                    {/* Show Assign button when no-link */}
                                    {(!shared && !started) && (
                                        <Button
                                            variant="default"
                                            size="sm"
                                            onClick={async (e) => {
                                                e.stopPropagation();
                                                await handleAssignAssessment();
                                            }}
                                            className="bg-action-blue hover:bg-action-blue-dark text-white"
                                        >
                                            Assign
                                        </Button>
                                    )}
                                    
                                    {/* Show Reveal/Hide Link and Cancel Link buttons when link-created */}
                                    {shared && (
                                        <>
                                            {assessmentLink && (
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        setIsLinkRevealed(prev => !prev);
                                                    }}
                                                    onMouseDown={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                    }}
                                                    className="text-action-blue border-action-blue/30 hover:bg-action-blue/15"
                                                >
                                                    <LinkIcon size={14} className="mr-1" />
                                                    {isLinkRevealed ? 'Hide Link' : 'Reveal Link'}
                                                </Button>
                                            )}
                                            {status?.e251_SharedAssessment?.e251_SharedAssessmentId && (
                                                <Button
                                                    variant="outline"
                                                    size="sm"
                                                    onClick={async (e) => {
                                                        e.stopPropagation();
                                                        if (status.e251_SharedAssessment?.e251_SharedAssessmentId) {
                                                            await sendCancelLinkCommand({
                                                                C210_SharedAssessmentId: status.e251_SharedAssessment.e251_SharedAssessmentId
                                                            });
                                                            if (onRefresh) {
                                                                onRefresh();
                                                            }
                                                        }
                                                    }}
                                                    className="text-destructive-action border-destructive-action/30 hover:bg-destructive-action/10"
                                                >
                                                    Cancel Link
                                                </Button>
                                            )}
                                        </>
                                    )}
                                    
                                    {/* Show Delete button at all times except when unit is completed */}
                                    {shouldShowDelete && unit.e251_UnitId && viewState !== viewStates.deletingAssessment && (
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setViewState(viewStates.deletingAssessment);
                                                setIsDeleting(true);
                                                setDeleteDialogOpen(true);
                                            }}
                                            className="text-destructive-action border-destructive-action/30 hover:bg-destructive-action/10"
                                        >
                                            <Trash2 size={14} className="mr-1" />
                                            Delete
                                        </Button>
                                    )}
                                    
                                    {/* Show deleting state */}
                                    {viewState === viewStates.deletingAssessment && (
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            disabled
                                            className="text-destructive-action border-destructive-action/30 hover:bg-destructive-action/10 opacity-50"
                                        >
                                            <Loader2 size={14} className="mr-1 animate-spin" />
                                            Deleting...
                                        </Button>
                                    )}
                                    
                                    {/* Show Delete Results button when started but not teacher-approved */}
                                    {shouldShowDeleteResults && status?.e251_Result?.e251_AssessmentResultId && (
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setDeleteResultsDialogOpen(true);
                                            }}
                                            className="text-destructive-action border-destructive-action/30 hover:bg-destructive-action/10"
                                        >
                                            <Trash2 size={14} className="mr-1" />
                                            Delete Results
                                        </Button>
                                    )}
                                    
                                    {/* Show Review Results button when started and needs-marking */}
                                    {shouldShowReviewResults && status?.e251_Result?.e251_AssessmentResultId && (
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                // Expand the card if it's collapsed
                                                if (!isExpanded && unit.e251_AssessmentId) {
                                                    onToggle(unit.e251_AssessmentId);
                                                }
                                                toggleAssessmentReview(status.e251_Result.e251_AssessmentResultId);
                                            }}
                                            className={confirmActionOutlineButtonClass}
                                        >
                                            {expandedAssessmentReviews.has(status.e251_Result.e251_AssessmentResultId) 
                                                ? 'Hide Answers' 
                                                : 'Review Results'}
                                        </Button>
                                    )}
                                    
                                    {/* Show Mark Assessment Completed button when in needs-marking state */}
                                    {resultState?.key === "needs-marking" && (
                                        <Button
                                            variant="default"
                                            size="sm"
                                            onClick={async (e) => {
                                                e.stopPropagation();
                                                if (status?.e251_Result?.e251_AssessmentResultId) {
                                                    await sendMarkAssessmentCompletedCommand({
                                                        C280_AssessmentResultId: status.e251_Result.e251_AssessmentResultId
                                                    });
                                                    onRefresh?.();
                                                }
                                            }}
                                            className="bg-action-blue hover:bg-action-blue-dark text-white"
                                        >
                                            Mark Assessment Completed
                                        </Button>
                                    )}
                                    
                                    {/* Show Retake button when unit is completed and assessment is teacher-approved */}
                                    {isUnitCompleted && resultState?.key === "teacher-approved" && unit.e251_AssessmentId && unit.e251_UnitId && unit.e251_StudentId && (
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            disabled={isRetaking}
                                            onClick={async (e) => {
                                                e.stopPropagation();
                                                setIsRetaking(true);
                                                try {
                                                    await sendRetakeAssessmentCommand({
                                                        C283_AssessmentId: unit.e251_AssessmentId,
                                                        C283_UnitId: unit.e251_UnitId,
                                                        C283_StudentId: unit.e251_StudentId
                                                    });
                                                } catch (error) {
                                                    setIsRetaking(false);
                                                }
                                                // Navigation and refresh will happen when E264v1_AssessmentCreated event is received
                                            }}
                                            className="text-action-blue border-action-blue/30 hover:bg-action-blue/15"
                                        >
                                            {isRetaking ? (
                                                <>
                                                    <Loader2 size={14} className="mr-1 animate-spin" />
                                                    Retaking...
                                                </>
                                            ) : (
                                                <>
                                                    <RotateCw size={14} className="mr-1" />
                                                    Retake
                                                </>
                                            )}
                                        </Button>
                                    )}
                                </div>
                            </div>

                            {/* Revealed Link Section */}
                            {isLinkRevealed && assessmentLink && (
                                <RevealedLinkDisplay link={assessmentLink} />
                            )}
                        </div>

                        {/* Assessment Content - Expanded */}
                        {/* Show preview OR review, not both */}
                        {/* When completed (teacher-approved), automatically show review section instead of preview */}
                        {isExpanded && unit.e251_AssessmentId && 
                         !(status?.e251_Result?.e251_AssessmentResultId && 
                           (expandedAssessmentReviews.has(status.e251_Result.e251_AssessmentResultId) ||
                            resultState?.key === "teacher-approved")) && (
                            <div className="px-4 pb-4 border-t border-gray-200">
                                <div className="pt-4">
                                    <AssessmentPreviewSection 
                                        assessmentId={unit.e251_AssessmentId}
                                        unitId={unit.e251_UnitId}
                                        assessmentPreviews={assessmentPreviews}
                                        setAssessmentPreviews={setAssessmentPreviews}
                                        isDeleting={isDeleting}
                                        previewEpoch={previewEpoch}
                                    />
                                </div>
                            </div>
                        )}
                        
                        {/* Assessment Review Section */}
                        {/* Show automatically when completed (teacher-approved), or when manually toggled, or when unit is completed */}
                        {isExpanded && status?.e251_Result?.e251_AssessmentResultId && 
                         (expandedAssessmentReviews.has(status.e251_Result.e251_AssessmentResultId) ||
                          (resultState?.key === "teacher-approved" || isUnitCompleted)) && (
                            <AssessmentReviewSection 
                                assessmentResultId={status.e251_Result.e251_AssessmentResultId}
                                assessmentResults={assessmentResults}
                                setAssessmentResults={setAssessmentResults}
                                isEditable={resultState?.key !== "teacher-approved" && !isUnitCompleted}
                            />
                        )}
                    </div>
                    
                    {/* Back side - Edit Assessment */}
                    {viewState === viewStates.editing && unit.e251_AssessmentId && (
                        <div className="bg-white rounded-lg">
                            <div className="p-4">
                                <div className="flex items-center justify-between mb-4">
                                    <h3 className="text-lg font-semibold text-gray-900">Edit Assessment</h3>
                                    <Button 
                                        variant="ghost" 
                                        size="sm" 
                                        onClick={() => setViewState(viewStates.viewing)}
                                    >
                                        <ArrowLeft size={16} className="mr-2" />
                                        Back
                                    </Button>
                                </div>
                                <EditAssessment 
                                    assessmentId={unit.e251_AssessmentId}
                                    selectedTab="questions"
                                />
                            </div>
                        </div>
                    )}
                </div>
            </Card>
            
            {/* Delete Assessment Dialog */}
            {deleteDialogOpen && unit.e251_UnitId && (
                <DeleteAssessmentDialog
                    unitId={unit.e251_UnitId}
                    assessmentTitle={unit.e251_Title}
                    onConfirm={() => {
                        // ViewState is already set to deletingAssessment
                    }}
                    onClose={() => {
                        setDeleteDialogOpen(false);
                        setViewState(viewStates.viewing);
                    }}
                />
            )}
            
            {/* Delete Assessment Results Dialog */}
            {deleteResultsDialogOpen && status?.e251_Result?.e251_AssessmentResultId && unit.e251_UnitId && (
                <DeleteAssessmentResultsDialog
                    assessmentResultId={status.e251_Result.e251_AssessmentResultId}
                    unitId={unit.e251_UnitId}
                    onConfirm={() => {
                        // After successful C282: always refresh units. Do not rely on E270 alone —
                        // see assessment SignalR onRefresh comment above.
                        onRefresh?.();
                    }}
                    onClose={() => {
                        setDeleteResultsDialogOpen(false);
                    }}
                />
            )}
            
            {/* Share By Email Dialog */}
            {shareByEmailDialogOpen && unit.e251_AssessmentId && (
                <ShareByEmailDialog
                    assessmentId={unit.e251_AssessmentId}
                    studentId={selectedStudentId || undefined}
                    unitId={unit.e251_UnitId}
                    open={shareByEmailDialogOpen}
                    onClose={() => {
                        setShareByEmailDialogOpen(false);
                    }}
                    onSuccess={() => {
                        setShareByEmailDialogOpen(false);
                    }}
                />
            )}
        </div>
    );
}

